/**
 * Moteur commun — empilements de cubes en perspective isométrique
 * (gens 1066 « vues » et 1067 « dénombrer »).
 *
 * h[r][c] : hauteur de la pile en rangée r (r = 0 : rangée de DEVANT) et
 * colonne c (c = 0 : à GAUCHE vu de face). L'observateur est devant à droite :
 * la face visible en bas à gauche est la FACE AVANT, celle en bas à droite le
 * côté DROIT. Sommet (c, rr, l) → écran : X = (c − rr)·W, Y = (c + rr)·Hh − l·S,
 * avec rr = R − 1 − r (la rangée de devant est la plus proche de l'observateur).
 */

export const cleH = (h) => h.map((row) => row.join('')).join('/');

const S = 20; const W_ISO = S * Math.cos(Math.PI / 6); const H_ISO = S / 2;
const f1 = (v) => v.toFixed(1);
const proj = (c, rr, l) => [(c - rr) * W_ISO, (c + rr) * H_ISO - l * S];

const dessinerCube = (c, rr, l) => {
    const P = (dc, dr, dl) => proj(c + dc, rr + dr, l + dl).map(f1).join(',');
    const haut = `${P(0, 0, 1)} ${P(1, 0, 1)} ${P(1, 1, 1)} ${P(0, 1, 1)}`;
    const avant = `${P(0, 1, 0)} ${P(1, 1, 0)} ${P(1, 1, 1)} ${P(0, 1, 1)}`;
    const droite = `${P(1, 0, 0)} ${P(1, 1, 0)} ${P(1, 1, 1)} ${P(1, 0, 1)}`;
    return `<polygon points="${haut}" fill="#dbeafe" stroke="#1e293b" stroke-width="1"/>`
        + `<polygon points="${avant}" fill="#93c5fd" stroke="#1e293b" stroke-width="1"/>`
        + `<polygon points="${droite}" fill="#60a5fa" stroke="#1e293b" stroke-width="1"/>`;
};

/**
 * Perspective d'un empilement. Options : { triedre: true } dessine les flèches
 * avant / droite / haut en bas à gauche ; { extra } ajoute des attributs au <svg>.
 */
export const svgPerspective = (h, o = {}) => {
    const R = h.length; const C = h[0].length; const hMax = Math.max(3, ...h.flat());
    const cubes = [];
    h.forEach((row, r) => row.forEach((hh, c) => { for (let l = 0; l < hh; l++) cubes.push({ c, rr: R - 1 - r, l }); }));
    // peintre : niveaux bas d'abord, puis du fond (c + rr petit) vers l'avant
    cubes.sort((a, b) => (a.l - b.l) || ((a.c + a.rr) - (b.c + b.rr)));
    let minX = Infinity; let maxX = -Infinity; let minY = Infinity; let maxY = -Infinity;
    const coins = [];
    for (let c = 0; c <= C; c++) for (let rr = 0; rr <= R; rr++) for (let l = 0; l <= hMax; l++) coins.push(proj(c, rr, l));
    coins.forEach(([x, y]) => { minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y); });
    const triedre = o.triedre !== false;
    const pad = 10; const triW = triedre ? 78 : 0;
    const w = Math.ceil(maxX - minX + 2 * pad + triW); const hh = Math.ceil(maxY - minY + 2 * pad + (triedre ? 8 : 0));
    const ox = -minX + pad + triW; const oy = -minY + pad;
    let tri = '';
    if (triedre) {
        // trièdre : avant (vers l'observateur, bas-gauche), droite (bas-droite), haut
        const TX = 38; const TY = hh - 22; const L = 20;
        const fleche = (x2, y2, txt, col, dx, dy) => {
            const ux = (x2 - TX) / Math.hypot(x2 - TX, y2 - TY); const uy = (y2 - TY) / Math.hypot(x2 - TX, y2 - TY);
            const nx = -uy; const ny = ux;
            return `<line x1="${TX}" y1="${TY}" x2="${f1(x2)}" y2="${f1(y2)}" stroke="${col}" stroke-width="1.6"/>`
                + `<polygon points="${f1(x2)},${f1(y2)} ${f1(x2 - 5 * ux + 2.5 * nx)},${f1(y2 - 5 * uy + 2.5 * ny)} ${f1(x2 - 5 * ux - 2.5 * nx)},${f1(y2 - 5 * uy - 2.5 * ny)}" fill="${col}"/>`
                + `<text x="${f1(x2 + dx)}" y="${f1(y2 + dy)}" font-size="9.5" font-weight="700" fill="${col}" text-anchor="middle">${txt}</text>`;
        };
        tri = `<circle cx="${TX}" cy="${TY}" r="2" fill="#1e293b"/>`
            + fleche(TX - L * Math.cos(Math.PI / 6), TY + L / 2, 'avant', '#7c3aed', -4, 11)
            + fleche(TX + L * Math.cos(Math.PI / 6), TY + L / 2, 'droite', '#dc2626', 6, 11)
            + fleche(TX, TY - L, 'haut', '#059669', 0, -5);
    }
    // echelle : facteur d'affichage (width/height), le viewBox reste en unités du dessin
    const e = o.echelle || 1;
    let s = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${hh}" width="${Math.round(w * e)}" height="${Math.round(hh * e)}" font-family="Arial" data-hauteurs="${cleH(h)}"${o.extra || ''}>${tri}<g transform="translate(${f1(ox)},${f1(oy)})">`;
    cubes.forEach(({ c, rr, l }) => { s += dessinerCube(c, rr, l); });
    return `${s}</g></svg>`;
};

/**
 * Aucun cube entièrement caché en perspective : pour chaque cube, au moins un
 * point d'une de ses trois faces visibles (dessus, avant, droite) doit être
 * atteint par un rayon vers l'observateur, de direction (1, 1, 1) dans le
 * repère (c, rr, l), sans traverser un autre cube. Sans cette garde, un
 * empilement dessiné ne déterminerait pas ses vues (retour capture 2026-09-04).
 */
export const aucunCubeCache = (h) => {
    const R = h.length; const cubes = [];
    h.forEach((row, r) => row.forEach((hh, c) => { for (let l = 0; l < hh; l++) cubes.push([c, R - 1 - r, l]); }));
    const traverse = (P, Y) => {
        let t0 = 1e-6; let t1 = Infinity;
        for (let i = 0; i < 3; i++) { t0 = Math.max(t0, Y[i] - P[i]); t1 = Math.min(t1, Y[i] + 1 - P[i]); }
        return t1 - t0 > 1e-6;
    };
    const ECH = [0.2, 0.5, 0.8];
    return cubes.every((X) => {
        const autres = cubes.filter((Y) => Y !== X);
        const points = [];
        ECH.forEach((a) => ECH.forEach((b) => {
            points.push([X[0] + a, X[1] + b, X[2] + 1]);      // dessus
            points.push([X[0] + a, X[1] + 1, X[2] + b]);      // avant
            points.push([X[0] + 1, X[1] + a, X[2] + b]);      // droite
        }));
        return points.some((P) => !autres.some((Y) => traverse(P, Y)));
    });
};
